import { defineStore } from "pinia";
import { markRaw } from "vue";
import api from "../services/api";
import config from "../config";
import { fetchReviewChunks } from "../util/reviewFilters";

// Every review on the platform, preloaded in the background when the admin
// boots (App.vue) and rendered by the Reviews page from here, stale-while-
// revalidate: a cache older than TTL_MS is shown at once and refreshed behind it.
// Kept in memory only — the rows carry reviewers' names and remarks — and not
// scoped per tenant, since the admin is global (ADR-0111).
const TTL_MS = 5 * 60 * 1000;
const CHUNK_SIZE = 1000;

// The load in flight, shared by everyone who asks while it runs.
let inflight = null;
// Exclusions an admin set while that load runs (ADR-0105). Its chunks may have
// been fetched before the PATCH, so they are laid over the result again — or
// the refresh would silently switch a just-excluded review back on (ADR-0111).
let setDuringLoad = new Map();

function withExclusions(reviews, exclusions) {
  if (exclusions.size === 0) return reviews;
  return reviews.map((review) => {
    const exclusion = exclusions.get(review.id);
    return exclusion ? { ...review, ...exclusion } : review;
  });
}

export const useReviewStore = defineStore("reviews", {
  state: () => ({
    reviews: [],
    fetchedAt: 0,
    // A load is running; with fetchedAt still 0 the page has nothing to show yet.
    loading: false,
  }),
  actions: {
    // Starts a full load, or joins the one already running.
    preload() {
      if (!inflight) {
        setDuringLoad = new Map();
        this.loading = true;
        inflight = fetchReviewChunks(
          (params) => api.get(`${config.url.api}/v1/reviews/0`, { params }).then((res) => res.data),
          CHUNK_SIZE
        )
          .then((reviews) => {
            // Read-only rows: markRaw spares Vue from proxying thousands of them.
            this.reviews = markRaw(withExclusions(reviews, setDuringLoad));
            this.fetchedAt = Date.now();
          })
          .finally(() => {
            this.loading = false;
            inflight = null;
            setDuringLoad = new Map();
          });
      }
      return inflight;
    },
    // An admin changed which halves of one review count (ADR-0105): patch that
    // row instead of reloading every review. A new array, because the rows are
    // markRaw — changing one in place would not re-render anything.
    applyExclusion({ id, activity_exclusion, specialist_exclusion }) {
      const exclusion = new Map([[id, { activity_exclusion, specialist_exclusion }]]);
      if (inflight) setDuringLoad.set(id, exclusion.get(id));
      this.reviews = markRaw(withExclusions(this.reviews, exclusion));
    },
    // What the Reviews page waits for: the first load, or nothing when there is
    // a cache — a stale one is refreshed in the background.
    ensure() {
      if (!this.fetchedAt) {
        return this.preload();
      }
      if (Date.now() - this.fetchedAt > TTL_MS) {
        this.preload().catch(() => {});
      }
      return Promise.resolve();
    },
  },
});
